import React, { FC } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks';
import HerdFormModal from './HerdFormModal';
import LoadingSpinner from './LoadingSpinner';
import HoverPreviewCard from './HoverPreviewCard';
import storageService from '../utils/localStorage';
import { Paper, Typography, useMediaQuery, Link, Button, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { handleJoinHerd, isJoined } from '../utils/JoinHerdToggle';
import type { SubState, UserState } from '../types/common';

const TopHerdsPanel: FC = () => {
  const herds = useAppSelector((state) => state.herds as SubState | null);
  const user = useAppSelector((state) => state.user as UserState | null);
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isNotDesktop = useMediaQuery(theme.breakpoints.down('lg'));

  if (isNotDesktop) {
    return null;
  }

  const loggedUser = storageService.loadUser() || user;
  const topHerds = herds?.topHerds ?? [];
  const loadingSubs = !herds?.topHerds;

  return (
    <Paper
      variant="outlined"
      sx={{
        minWidth: '25%',
        borderRadius: 'var(--radius-none)',
        marginLeft: 'var(--space-stack-tight)',
        padding: 'var(--space-stack-tight)',
        // Stay pinned (static) as the feed scrolls, just below the fixed glass bar.
        position: 'sticky',
        top: 'var(--space-stack-tight)',
        maxHeight: 'calc(100vh - var(--layout-top-bar-height) - var(--space-stack-large))',
        overflowY: 'auto',
      }}
    >
      <Paper variant="outlined" sx={{ padding: 'var(--space-stack-normal)' }}>
        <Typography variant="h5" color="secondary" sx={{ textAlign: 'center', marginBottom: 'var(--space-stack-normal)' }}>
          Top Herds
        </Typography>
        {loadingSubs ? (
          <LoadingSpinner text="Fetching herds data..." />
        ) : (
          topHerds.map((s, i) => (
            <Box key={s.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-stack-normal)' }}>
              <Typography variant="body2" sx={{ fontSize: 'bodyEmphasis' }}>
                {`${i + 1}. `}
                <HoverPreviewCard type="herd" name={s.herdName}>
                  <Link component={RouterLink} to={`/herd/${s.herdName}`} color="primary">
                    {s.herdName}
                  </Link>
                </HoverPreviewCard>
                {` - ${s.memberCount ?? 0} members `}
              </Typography>
              {loggedUser && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={isJoined(s.id, user) ? <CheckIcon /> : <AddIcon />}
                  onClick={() => handleJoinHerd(s.id, s.herdName, dispatch, user)}
                >
                  {isJoined(s.id, user) ? 'Joined' : 'Join'}
                </Button>
              )}
            </Box>
          ))
        )}
      </Paper>
      {loggedUser && <HerdFormModal />}
    </Paper>
  );
};

export default TopHerdsPanel;
